import { useMemo, useState, useTransition } from 'react';
import { fetchJson } from '@scripts/lib/fetch-json';
import { classNames, formatCurrency } from '@scripts/lib/formatters';

/**
 * Mount target:
 *   <div data-island="mini-cart" data-props="{...}"></div>
 *
 * Props contract:
 * - title: string
 * - emptyMessage: string
 * - subtotalLabel: string
 * - viewCartLabel: string
 * - checkoutLabel: string
 * - currency: string
 * - locale: string
 * - items: Array<{ key?: string, id?: number, name: string, quantity: number, price: number, image?: string, permalink?: string }>
 * - subtotal: number
 * - actions: { viewCartUrl?: string, checkoutUrl?: string }
 * - endpoints?: { updateQuantity?: string, removeItem?: string }
 *
 * Trigger conditions:
 * - Render this island in a WooCommerce mini-cart drawer, header cart popover,
 *   or cart summary widget when asynchronous quantity updates are desired.
 */
export function MiniCartIsland({
  title = 'Mini Cart',
  emptyMessage = 'Your cart is currently empty.',
  subtotalLabel = 'Subtotal',
  viewCartLabel = 'View Cart',
  checkoutLabel = 'Checkout',
  removeLabel = 'Remove',
  decreaseQuantityLabel = 'Decrease quantity',
  increaseQuantityLabel = 'Increase quantity',
  currency = 'USD',
  locale = 'en',
  items = [],
  subtotal = 0,
  actions = {},
  endpoints = {},
}) {
  const [cartItems, setCartItems] = useState(() =>
    Array.isArray(items)
      ? items.map((item, index) => ({
          key: item.key || item.id || `item-${index}`,
          quantity: Number(item.quantity || 0),
          price: Number(item.price || 0),
          ...item,
        }))
      : []
  );
  const [isPending, startTransition] = useTransition();

  const derivedSubtotal = useMemo(() => {
    if (cartItems.length === 0) {
      return Number(subtotal || 0);
    }

    return cartItems.reduce((sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 0), 0);
  }, [cartItems, subtotal]);

  function syncCart(endpoint, payload) {
    if (!endpoint) {
      return Promise.resolve(null);
    }

    return fetchJson(endpoint, {
      method: 'POST',
      body: JSON.stringify(payload),
    }).catch((error) => {
      console.warn('Mini cart sync failed.', error);
      return null;
    });
  }

  function updateQuantity(targetKey, nextQuantity) {
    startTransition(() => {
      setCartItems((currentItems) =>
        currentItems
          .map((item) => {
            if (item.key !== targetKey) {
              return item;
            }

            return {
              ...item,
              quantity: Math.max(1, nextQuantity),
            };
          })
          .filter((item) => item.quantity > 0)
      );
    });

    void syncCart(endpoints.updateQuantity, {
      key: targetKey,
      quantity: Math.max(1, nextQuantity),
    });
  }

  function removeItem(targetKey) {
    startTransition(() => {
      setCartItems((currentItems) => currentItems.filter((item) => item.key !== targetKey));
    });

    void syncCart(endpoints.removeItem, {
      key: targetKey,
    });
  }

  return (
    <section className="island-panel space-y-5" aria-busy={isPending}>
      <header className="flex items-center justify-between gap-4">
        <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
        <span className="text-sm text-slate-500">{cartItems.length}</span>
      </header>

      {cartItems.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-slate-300 px-4 py-6 text-sm text-slate-500">
          {emptyMessage}
        </p>
      ) : (
        <div className="space-y-4">
          {cartItems.map((item) => (
            <article key={item.key} className="flex items-start gap-4 rounded-2xl border border-slate-200 p-4">
              {item.image ? (
                <img
                  src={item.image}
                  alt={item.name}
                  className="h-16 w-16 rounded-xl object-cover"
                  loading="lazy"
                />
              ) : null}

              <div className="min-w-0 flex-1 space-y-2">
                <div className="space-y-1">
                  <h4 className="text-sm font-semibold text-slate-900">
                    {item.permalink ? (
                      <a href={item.permalink} className="hover:text-brand-700">
                        {item.name}
                      </a>
                    ) : (
                      item.name
                    )}
                  </h4>
                  <p className="text-sm text-slate-500">{formatCurrency(item.price, currency, locale)}</p>
                </div>

                <div className="flex items-center justify-between gap-3">
                  <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-2 py-1">
                    <button
                      type="button"
                      className="h-8 w-8 rounded-full text-slate-700 transition hover:bg-slate-100"
                      onClick={() => updateQuantity(item.key, item.quantity - 1)}
                      aria-label={`${decreaseQuantityLabel}: ${item.name}`}
                    >
                      -
                    </button>
                    <span className="min-w-8 text-center text-sm font-medium text-slate-900">
                      {item.quantity}
                    </span>
                    <button
                      type="button"
                      className="h-8 w-8 rounded-full text-slate-700 transition hover:bg-slate-100"
                      onClick={() => updateQuantity(item.key, item.quantity + 1)}
                      aria-label={`${increaseQuantityLabel}: ${item.name}`}
                    >
                      +
                    </button>
                  </div>

                  <button
                    type="button"
                    className="text-sm font-medium text-slate-500 transition hover:text-rose-600"
                    onClick={() => removeItem(item.key)}
                  >
                    {removeLabel}
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      <footer className="space-y-4 border-t border-slate-200 pt-4">
        <div className="flex items-center justify-between gap-4 text-sm">
          <span className="font-medium text-slate-600">{subtotalLabel}</span>
          <span className="text-base font-semibold text-slate-900">
            {formatCurrency(derivedSubtotal, currency, locale)}
          </span>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <a
            href={actions.viewCartUrl || '#'}
            className={classNames(
              'inline-flex min-h-11 items-center justify-center rounded-full border border-slate-300 px-4 text-sm font-medium text-slate-700 transition hover:border-brand-300 hover:text-brand-700',
              !actions.viewCartUrl && 'pointer-events-none opacity-50'
            )}
          >
            {viewCartLabel}
          </a>
          <a
            href={actions.checkoutUrl || '#'}
            className={classNames('button-primary min-h-11', !actions.checkoutUrl && 'pointer-events-none opacity-50')}
          >
            {checkoutLabel}
          </a>
        </div>
      </footer>
    </section>
  );
}
